import React, { useState, useCallback } from 'react';
import styled from 'styled-components';

import Star from './Star';
import { media } from '@/styles/theme';

interface StarRatingProps {
  primaryColor: string;
  setStarRating: (rating: number) => void;
}

const OuterContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
`;

const InnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 660px;

  ${media.tablet} {
    flex-direction: row;
    align-items: flex-start;
    justify-content: center;
  }
`;

const Title = styled.h3<{ $color: string }>`
  margin: 0;
  font-size: 20px;
  // width: 200px;
  text-align: center;
  margin-bottom: 36px;
  color: ${({ $color }) => $color};

  ${media.tablet} {
    font-size: 28px;
    text-align: right;
    //width: 220px;
    padding-right: 34px;
    margin-right: 24px;
    margin-bottom: 0;
    border-right: 4px solid ${({ theme }) => theme.colors.border};
  }
`;

const getStarLabelColor = (starNumber: number): string => {
  switch (starNumber) {
    case 1:
      return '#e74c3c'; // Red for Poor
    case 2:
      return '#e67e22'; // Orange-red (between red and gray)
    case 3:
      return '#95a5a6'; // Gray for OK
    case 4:
      return '#27ae60'; // Light green (between gray and green)
    case 5:
      return '#16a085'; // Green for Great!
    default:
      return '#95a5a6'; // Default gray
  }
};

const StarLabel = styled.span<{
  $isDefaultVisible: boolean;
  $starNumber: number;
}>`
  cursor: pointer;
  position: absolute;
  left: 50%;
  top: 100%;
  transform: translateX(-50%);
  width: 100%;
  background-color: white;
  padding-top: 16px;
  letter-spacing: 1px;
  text-transform: uppercase;
  text-align: center;
  font-size: 9px;
  transition-duration: ${({ theme }) => theme.transitionDuration};

  /* Default state: hide all labels except Poor and Great! */
  opacity: ${({ $isDefaultVisible }) => ($isDefaultVisible ? 0.8 : 0)};
  font-weight: ${({ $isDefaultVisible }) =>
    $isDefaultVisible ? 'bold' : 'normal'};

  /* Color based on star rating sentiment */
  color: ${({ $starNumber }) => getStarLabelColor($starNumber)};

  ${media.mobileLandscape} {
    font-size: 11px;
  }

  ${media.tablet} {
    font-size: 12px;
  }
`;

const StarsWrapper = styled.div`
  display: flex;
  max-width: 375px;
  max-height: 64px;

  /* On hover over any star, hide all labels */
  &:hover ${StarLabel} {
    opacity: 0;
  }
`;

const StarButton = styled.button`
  position: relative;
  background: none;
  border: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 20%;
  padding: 0 2%;
  cursor: pointer;

  /* On hover over this specific star, show only its label */
  &:hover ${StarLabel} {
    opacity: 0.8;
    font-weight: bold;
  }
`;

const StarRatingScreen: React.FC<StarRatingProps> = ({
  primaryColor,
  setStarRating,
}) => {
  const [starHovered, setStarHovered] = useState<number | null>(null);
  const [freeze] = useState(false);

  const setHovered = useCallback(
    (num: number, isHovered: boolean) => {
      if (freeze) return;
      setStarHovered(isHovered ? num : null);
    },
    [freeze]
  );

  const handleStarClick = useCallback(
    (num: number) => {
      setStarRating(num);
    },
    [setStarRating]
  );

  const starLabels = ['Poor', 'Not Good', 'OK', 'Good', 'Great!'];

  return (
    <OuterContainer>
      <InnerContainer>
        <Title $color={primaryColor}>
          How would you rate your overall experience?
        </Title>
        <StarsWrapper>
          {[1, 2, 3, 4, 5].map((num) => {
            const isDefaultVisible = num === 1 || num === 5; // Only show Poor and Great! by default

            return (
              <StarButton
                key={num}
                onMouseEnter={() => setHovered(num, true)}
                onMouseLeave={() => setHovered(num, false)}
                onClick={() => handleStarClick(num)}
              >
                <Star
                  isActive={starHovered !== null ? starHovered >= num : false}
                  highlightOnMobile={false}
                />
                <StarLabel
                  $isDefaultVisible={isDefaultVisible}
                  $starNumber={num}
                >
                  {starLabels[num - 1]}
                </StarLabel>
              </StarButton>
            );
          })}
        </StarsWrapper>
      </InnerContainer>
    </OuterContainer>
  );
};

export default StarRatingScreen;
